import { useCallback, useEffect, useState } from "react";
import { Input } from "../../../components/ui";
import { COLORS } from "../../../theme/colors";
import { widgetApi } from "../../../api/client";

/**
 * Per-agent "Allowed domains" manager for the embed modal.
 *
 * The prod incident this closes: the widget runs on *customer* origins, and
 * prod CORS is locked to the platform origin (SEC-004), so an embedded widget
 * can't call the API until its domain is allow-listed. This panel is where the
 * admin lists those domains.
 *
 * FAIL-OPEN is the load-bearing UX fact and must stay visible: an EMPTY list
 * means the widget works on ANY site (unrestricted). Adding the first domain is
 * what starts restricting it. Admins who expect "empty = blocked" would lock
 * themselves out mentally, so the empty state says this explicitly.
 *
 * Split from EmbedSnippetModal so the fetch/add/remove/allow logic is unit-
 * testable without mounting the whole modal.
 */
export default function WidgetAllowedDomains({ agentId }) {
  const [allowed, setAllowed] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [newOrigin, setNewOrigin] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const [a, b] = await Promise.all([
        widgetApi.listAllowedOrigins(agentId),
        widgetApi.listBlockedOrigins(agentId),
      ]);
      setAllowed((a?.data || a) ?? []);
      setBlocked((b?.data || b) ?? []);
    } catch (e) {
      setError(e?.message || "Failed to load allowed domains");
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleAdd() {
    const origin = newOrigin.trim();
    if (!origin || busy) return;
    setBusy(true);
    setError(null);
    try {
      await widgetApi.addAllowedOrigin(origin, agentId);
      setNewOrigin("");
      await load();
    } catch (e) {
      // The backend returns 400 with an actionable message on an invalid
      // pattern (e.g. a bare "*.com" or a path) — surface it verbatim.
      setError(e?.message || "Could not add that domain");
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(id) {
    setBusy(true);
    setError(null);
    try {
      await widgetApi.deleteAllowedOrigin(id);
      await load();
    } catch (e) {
      setError(e?.message || "Could not remove that domain");
    } finally {
      setBusy(false);
    }
  }

  async function handleAllowBlocked(blockedId) {
    setBusy(true);
    setError(null);
    try {
      await widgetApi.allowBlockedOrigin(blockedId);
      await load();
    } catch (e) {
      setError(e?.message || "Could not allow that domain");
    } finally {
      setBusy(false);
    }
  }

  const restricted = allowed.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: COLORS.textMuted }}>
          Allowed domains
        </span>
        <span
          data-testid="wad-status"
          style={{ fontSize: 10, color: restricted ? COLORS.green : COLORS.textDim }}
        >
          {restricted ? `Restricted to ${allowed.length} domain${allowed.length > 1 ? "s" : ""}` : "Unrestricted (any site)"}
        </span>
      </div>

      {/* Fail-open explainer — the empty state MUST not read as "blocked". */}
      <p style={{ fontSize: 11, color: COLORS.textMuted, margin: 0, lineHeight: 1.5 }}>
        {restricted
          ? "This widget only loads on the domains below. Remove them all to make it work everywhere again."
          : "No domains listed — this widget works on any website. Add a domain to restrict it to only the sites you own."}
      </p>

      {/* Add row */}
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Input
            value={newOrigin}
            onChange={(e) => setNewOrigin(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }}
            placeholder="https://example.com  or  https://*.example.com"
            aria-label="New allowed domain"
          />
        </div>
        <button
          onClick={handleAdd}
          disabled={busy || !newOrigin.trim()}
          style={{
            padding: "7px 14px", borderRadius: 8, border: "none",
            background: COLORS.accent, color: "#fff", fontSize: 12, fontWeight: 600,
            cursor: busy || !newOrigin.trim() ? "default" : "pointer",
            opacity: busy || !newOrigin.trim() ? 0.6 : 1, whiteSpace: "nowrap",
          }}
        >
          Add
        </button>
      </div>

      {/* Allowed list */}
      {allowed.length > 0 && (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {allowed.map((o) => (
            <li key={o.id} data-testid="wad-allowed-row" style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "6px 10px", borderRadius: 8, background: COLORS.bg,
              border: `1px solid ${COLORS.border}`,
            }}>
              <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, color: COLORS.text }}>
                {o.origin}
                {o.agent_id === null && (
                  <span style={{ marginLeft: 8, fontSize: 9, color: COLORS.textDim, fontFamily: "inherit" }}>
                    (all agents)
                  </span>
                )}
              </span>
              <button
                onClick={() => handleRemove(o.id)}
                disabled={busy}
                aria-label={`Remove ${o.origin}`}
                style={{
                  padding: "2px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`,
                  background: "transparent", color: COLORS.textMuted, fontSize: 11,
                  cursor: busy ? "default" : "pointer",
                }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Blocked-recently → Allow callout */}
      {blocked.length > 0 && (
        <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: COLORS.textMuted }}>
            Blocked recently
          </span>
          <p style={{ fontSize: 11, color: COLORS.textMuted, margin: 0, lineHeight: 1.5 }}>
            The widget tried to load from these sites but they aren't allowed. If you recognise one, allow it.
          </p>
          {blocked.map((b) => (
            <div key={b.id} data-testid="wad-blocked-row" style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "6px 10px", borderRadius: 8,
              background: `${COLORS.amber || "#f59e0b"}12`,
              border: `1px solid ${COLORS.amber || "#f59e0b"}35`,
            }}>
              <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, color: COLORS.text }}>
                {b.origin}
                <span style={{ marginLeft: 8, fontSize: 9, color: COLORS.textDim, fontFamily: "inherit" }}>
                  ×{b.hit_count}
                </span>
              </span>
              <button
                onClick={() => handleAllowBlocked(b.id)}
                disabled={busy}
                style={{
                  padding: "2px 10px", borderRadius: 6, border: "none",
                  background: COLORS.green, color: "#fff", fontSize: 11, fontWeight: 600,
                  cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
                }}
              >
                Allow
              </button>
            </div>
          ))}
        </div>
      )}

      {loading && <span style={{ fontSize: 10, color: COLORS.textDim }}>Loading…</span>}
      {error && (
        <div style={{
          padding: "8px 12px", borderRadius: 8,
          background: `${COLORS.red || "#ef4444"}15`,
          border: `1px solid ${COLORS.red || "#ef4444"}40`,
          color: COLORS.red || "#ef4444", fontSize: 12,
        }}>{error}</div>
      )}
    </div>
  );
}
